// src/api/authSession.js
import { resolveLobbyApiBase } from '../config/serviceUrl';

const API_BASE = resolveLobbyApiBase();
const REFRESH_BEFORE_MS = Math.max(60_000, Number(process.env.REACT_APP_AUTH_REFRESH_BEFORE_MS || 10 * 60 * 1000));
const AUTH_REFRESH_TIMEOUT_MS = Math.max(2500, Number(process.env.REACT_APP_AUTH_REFRESH_TIMEOUT_MS || 6000) || 6000);
const LOGIN_SESSION_STORAGE_KEY = 'npc:auth:session_id';

const DEFINITIVE_AUTH_ERRORS = new Set([
  'NO_TOKEN',
  'SESSION_MISSING',
  'SESSION_EXPIRED',
  'SESSION_MISMATCH',
  'SESSION_INVALID',
  'TOKEN_EXPIRED',
  'INVALID_TOKEN',
  'TOKEN_REVOKED',
  'ACCOUNT_NOT_FOUND',
]);

let refreshPromise = null;

export function getStoredAccessToken() {
  try {
    return (
      localStorage.getItem('token') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('jwt') ||
      localStorage.getItem('authToken') ||
      ''
    );
  } catch (_) {
    return '';
  }
}

export function decodeJwtPayload(token = '') {
  try {
    const part = String(token || '').split('.')[1] || '';
    if (!part) return null;
    const normalized = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

export function getAccessTokenSessionId(token = getStoredAccessToken()) {
  const payload = decodeJwtPayload(token);
  return String(payload?.login_session_id || payload?.sid || payload?.session_id || '').trim();
}

export function getStoredLoginSessionId() {
  let stored = '';
  try { stored = String(localStorage.getItem(LOGIN_SESSION_STORAGE_KEY) || '').trim(); } catch (_) {}
  const fromToken = getAccessTokenSessionId(getStoredAccessToken());

  // Token is the source of truth. Repair the explicit cache if an older build
  // wrote the token without the companion session id.
  if (fromToken) {
    if (stored !== fromToken) {
      try { localStorage.setItem(LOGIN_SESSION_STORAGE_KEY, fromToken); } catch (_) {}
    }
    return fromToken;
  }
  return stored;
}

export function persistAccessToken(token = '', sessionId = '') {
  const nextToken = String(token || '').trim();
  if (!nextToken) return '';
  const sid = String(sessionId || getAccessTokenSessionId(nextToken) || '').trim();
  try { localStorage.setItem('token', nextToken); } catch (_) {}
  if (sid) {
    try { localStorage.setItem(LOGIN_SESSION_STORAGE_KEY, sid); } catch (_) {}
  }
  return sid;
}

export function clearStoredLoginSessionId() {
  try { localStorage.removeItem(LOGIN_SESSION_STORAGE_KEY); } catch (_) {}
}

export function isCurrentLoginSession(sessionId = '') {
  const candidate = String(sessionId || '').trim();
  if (!candidate) return false;
  const current = getStoredLoginSessionId();
  return !!current && current === candidate;
}

export function getAccessTokenRemainingMs(token = getStoredAccessToken()) {
  const payload = decodeJwtPayload(token);
  const expMs = Number(payload?.exp || 0) * 1000;
  if (!expMs) return Number.POSITIVE_INFINITY;
  return expMs - Date.now();
}

function buildRefreshError(status, data = {}, cause = null, requestSessionId = '') {
  const err = new Error('AUTH_REFRESH_FAILED');
  err.status = Number(status || 0);
  err.code = String(data?.error || '').trim();
  err.data = data || {};
  err.cause = cause || undefined;
  err.requestSessionId = String(
    data?.request_session_id ||
    data?.session_id ||
    requestSessionId ||
    ''
  ).trim();
  err.currentSessionId = String(data?.current_session_id || '').trim();
  // Downstream screen restoration uses Axios-style response.status to distinguish
  // a temporary refresh outage from a definitive 401 login failure.
  if (err.status > 0) err.response = { status: err.status, data: err.data };
  err.transient = err.status === 0 || err.status >= 500 || err.code === 'SESSION_STORE_UNAVAILABLE';
  return err;
}

export function isDefinitiveAuthFailure(error) {
  const code = String(error?.code || error?.response?.data?.error || error?.message || '').trim();
  const status = Number(error?.status || error?.response?.status || 0);
  if (code === 'SESSION_STORE_UNAVAILABLE' || status >= 500 || status === 0) return false;
  return status === 401 && DEFINITIVE_AUTH_ERRORS.has(code);
}

export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const currentToken = getStoredAccessToken();
    const requestSessionId = getAccessTokenSessionId(currentToken) || getStoredLoginSessionId();
    if (!currentToken) throw buildRefreshError(401, { error: 'NO_TOKEN' }, null, requestSessionId);

    let response;
    let data = {};
    try {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = controller
        ? setTimeout(() => controller.abort(), AUTH_REFRESH_TIMEOUT_MS)
        : null;
      try {
        response = await fetch(`${String(API_BASE || '').replace(/\/$/, '')}/user/session/refresh`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${currentToken}`,
            'Content-Type': 'application/json',
          },
          body: '{}',
          cache: 'no-store',
          credentials: 'same-origin',
          ...(controller ? { signal: controller.signal } : {}),
        });
      } finally {
        if (timer) clearTimeout(timer);
      }
      try { data = await response.json(); } catch (_) { data = {}; }
    } catch (cause) {
      throw buildRefreshError(0, { error: 'AUTH_REFRESH_NETWORK_ERROR' }, cause, requestSessionId);
    }

    if (!response.ok || !data?.token) {
      throw buildRefreshError(response.status, data, null, requestSessionId);
    }

    const nextToken = String(data.token || '').trim();
    const responseSessionId = String(data?.session_id || getAccessTokenSessionId(nextToken) || requestSessionId || '').trim();
    const latestSessionId = getStoredLoginSessionId();

    // The refresh request belongs to the session that existed when it started.
    // If a new login replaced that session while this request was in flight,
    // never overwrite the new token with the stale refresh response.
    if (requestSessionId && latestSessionId && latestSessionId !== requestSessionId) {
      return getStoredAccessToken();
    }
    if (requestSessionId && responseSessionId && responseSessionId !== requestSessionId) {
      throw buildRefreshError(401, {
        error: 'SESSION_MISMATCH',
        request_session_id: requestSessionId,
        current_session_id: latestSessionId || responseSessionId,
      }, null, requestSessionId);
    }

    persistAccessToken(nextToken, responseSessionId);
    try {
      window.dispatchEvent(new CustomEvent('npc:auth-token-refreshed', {
        detail: { token: nextToken, session_id: responseSessionId, ts: Date.now() },
      }));
    } catch (_) {}
    return nextToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

export async function ensureFreshAccessToken(options = {}) {
  const token = getStoredAccessToken();
  if (!token) return '';
  const minValidityMs = Math.max(0, Number(options.minValidityMs ?? REFRESH_BEFORE_MS));
  const remainingMs = getAccessTokenRemainingMs(token);
  if (options.force !== true && remainingMs > minValidityMs) return token;
  return refreshAccessToken();
}
